import { createSlice } from '@reduxjs/toolkit';
//import cloneDeep from 'lodash/cloneDeep';

export interface AppDescriptor {
  platform:			string;
  id:				string;
  name:				string;
  logo:				string;
  install_id:		string;
}

export interface ProviderCreds {
  username:			string;
  password:			string;
}

export interface ProviderUrl {
  type:				string;
  url:				string;
  [key: string]:	any;
}

export interface ProviderConfig {
  name:				string;
  type:				string;
  base_url:			string;
  country?:			string;
  disabled?:		boolean;
  creds_set?:		boolean;	// read-only flag from GET; never send back
  creds?:			ProviderCreds;	// write-only on POST; OMIT to keep the stored creds
  urls?:			ProviderUrl[];	// keyed options; NOT server-merged — see below
}

// Adapter selectors the backend knows about.
export const PROVIDER_TYPES = [
  { value: 'kpn-marvin', desc: 'KPN (Marvin)' },
];

// Prefilled when the admin picks a type for a fresh instance.
export const PROVIDER_BASE_URLS: { [type: string]: string } = {
  'kpn-marvin':		'https://marvin.bcms-cdn.prod.aws.kpn.com',
};
export const PROVIDER_URL_FALLBACK = 'fall-back';

export function copyProviderUrls(urls: any): ProviderUrl[] {
  return	Array.isArray(urls)
  			?	urls
  					.filter((u: any) => u && typeof u === 'object')
  					.map((u: any) => ({ ...u }))
  			:	[];
}

export function providerFallbackEnabled(prov: ProviderConfig): boolean {
  return (prov.urls || []).some(
    (u) => u.type === PROVIDER_URL_FALLBACK && u.url === "1"
  );
}

export function setProviderFallback(urls: ProviderUrl[] | undefined, enabled: boolean): ProviderUrl[] {
  const rest = copyProviderUrls(urls).filter((u) => u.type !== PROVIDER_URL_FALLBACK);

  return	enabled
  			?	[...rest, { type: PROVIDER_URL_FALLBACK, url: "1" }]
  			:	rest;
}

// ---------------------------------------------------------------------------
// EPG sources
//
// One XMLTV feed in a PRIORITY-ORDERED list. The URL is the identity; `name`
// is a cosmetic admin label. `disabled` is `omitempty` on the wire, so an
// ABSENT flag means ENABLED — the same convention as ProviderConfig.
//
// The backend fetches every enabled feed independently and merges them top to
// bottom: the first enabled feed that supplies a channel id owns that channel
// and its programmes. Order is therefore a real setting, not presentation.
// ---------------------------------------------------------------------------
export interface EpgSource {
  name?:			string;
  url:				string;
  disabled?:		boolean;
}

// POST /master.json rejects the whole save above this count (epg_max_sources).
export const EPG_MAX_SOURCES = 8;

export const epgStatusUrl = "/epg_status.json";

// Per-feed health from GET /epg_status.json. NOTE the two counterintuitive
// fields: `ok` means "has usable data" and stays true through an outage, and
// `channels`/`programmes` are what this feed CONTRIBUTED to the merged guide,
// not what it published.
export interface EpgSourceStatus {
  url:				string;
  name?:			string;
  ok:				boolean;
  stale?:			boolean;
  last_success?:	string;	// RFC3339; absent = never fetched successfully
  last_attempt?:	string;	// RFC3339
  error?:			string;
  channels?:		number;
  programmes?:		number;
  dropped_duplicates?:	number;	// rows a HIGHER feed already owned
}

export interface EpgStatus {
  sources:			EpgSourceStatus[];
  last_update?:		string;	// absent until the first successful load
  channels?:		number;
  programmes?:		number;
}

export function copyEpgSource(src: any): EpgSource {
  return {
    name:			src && src.name ? String(src.name) : "",
    url:			src && src.url ? String(src.url) : "",
    disabled:		!!(src && src.disabled),
  };
}

// `epg_sources` is always an array on GET — never null, never absent — and it
// is the RESOLVED list, so a deployment that never saved one gets its legacy
// `epg_url` back as a single synthesised row. Do NOT add an epg_url fallback
// on top of this; the server already did it.
export function copyEpgSources(sources: any): EpgSource[] {
  return	Array.isArray(sources)
  			?	sources
  					.filter((s: any) => s && typeof s === "object")
  					.map(copyEpgSource)
  			:	[];
}

// Normalise a raw /epg_status.json body. The endpoint returns a RAW object,
// not the {errors:[...]} envelope, and must never be routed through
// apiOperation — see the poll in pages/Settings/Epg.tsx.
export function copyEpgStatus(raw: any): EpgStatus {
  const sources = Array.isArray(raw?.sources) ? raw.sources : [];

  return {
    sources:		sources
    					.filter((s: any) => s && typeof s === "object")
    					.map((s: any) => ({
    						url:			s.url || "",
    						name:			s.name || "",
    						ok:				!!s.ok,
    						stale:			!!s.stale,
    						last_success:	s.last_success || "",
    						last_attempt:	s.last_attempt || "",
    						error:			s.error || "",
    						channels:		typeof s.channels === "number" ? s.channels : 0,
    						programmes:		typeof s.programmes === "number" ? s.programmes : 0,
    						dropped_duplicates:	typeof s.dropped_duplicates === "number"
    												?	s.dropped_duplicates
    												:	0,
    					})),
    last_update:	raw?.last_update || "",
    channels:		typeof raw?.channels === "number" ? raw.channels : 0,
    programmes:		typeof raw?.programmes === "number" ? raw.programmes : 0,
  };
}

export const DEVICE_INFO_VERSION_DEFAULT = "2.0";
export const DEVICE_INFO_REFRESH_HOURS_DEFAULT = 24;

export const skywayRefreshUrl = "/skyway_refresh.json";

// Snapshot returned by POST /skyway_refresh.json. It reports the cache state
// BEFORE the queued reload is applied.
export interface DeviceInfoSnapshot {
  queued:			boolean;
  total:			number;
  last_update:		string;	// RFC 3339 UTC, or "" when no load has completed
  source:			string;	// "api" | "file" | "empty" — open string
  error?:			string;	// message of the most recent load failure
}

export type SkywayRefreshResult =
  | { ok: true;  snapshot: DeviceInfoSnapshot }
  | { ok: false; status: number };

// #91 §0.8 / §4.1 — one row of `checkout_deviations`.
export interface CheckoutDeviation {
  id:					string;
  name:					string;
  checkout_enabled:		boolean;	// the hotel's own value
}

export interface MasterInfo {
  // Legacy single-feed field. Now a SERVER-DERIVED mirror of the first enabled
  // source, kept so an older admin page or a downgraded binary still finds a
  // working feed. A bare write is IGNORED once `epg_sources` has been saved,
  // so it must not be offered as an editable control.
  epg_url:			string;
  epg_sources?:		EpgSource[];
  smtp_host?:			string;
  smtp_port?:			number;
  smtp_username?:		string;
  smtp_password?:		string;	// write-only on POST; never returned by GET
  smtp_password_set?:	boolean;	// read-only flag from GET; do not send back
  smtp_from?:			string;
  smtp_from_name?:		string;
  smtp_starttls?:		boolean;
  public_base_url?:	string;
  global_2fa_disabled?:	boolean;
  // The Checkout switch (ticket #91 rev 2, §0.1). Read it through
  // checkoutSwitchedOff(), never directly: `undefined` before the first read
  // means "not known yet". It turns off ONLY the Checkout; MW light stays.
  checkout_enabled?:	boolean;
  // #91 §0.8 — read-only: the hotels whose own Checkout value differs from
  // checkout_enabled. Never sent back (a POST ignores it anyway).
  checkout_deviations?:	CheckoutDeviation[];
  providers?:			ProviderConfig[];
  device_info_api_url?:		string;
  device_info_api_key?:		string;
  device_info_api_secret?:	string;		// write-only on POST; send empty to keep
  device_info_api_secret_set?:	boolean;	// read-only flag from GET; never send back
  device_info_version?:		string;
  device_info_refresh_hours?:	number;
  deleted_retention_days?:	number;
  audit_retention_days?:	number;
  loading?:			boolean;
}

export const DELETED_RETENTION_DEFAULT_DAYS = 180;
export const AUDIT_RETENTION_DEFAULT_DAYS = 180;

export const masterUrl = "/master.json";

const initialState: MasterInfo = getNewMasterInfo(true);

export const masterSlice = createSlice({
  name: 'master',
  initialState,
  reducers: {
    assign: (state, action) => {
      state.epg_url = action.payload.epg_url;
      state.epg_sources = copyEpgSources(action.payload.epg_sources);
      state.smtp_host = action.payload.smtp_host || "";
      state.smtp_port = action.payload.smtp_port || 0;
      state.smtp_username = action.payload.smtp_username || "";
      state.smtp_password = "";
      state.smtp_password_set = !!action.payload.smtp_password_set;
      state.smtp_from = action.payload.smtp_from || "";
      state.smtp_from_name = action.payload.smtp_from_name || "";
      state.smtp_starttls = !!action.payload.smtp_starttls;
      state.public_base_url = action.payload.public_base_url || "";
      state.global_2fa_disabled = !!action.payload.global_2fa_disabled;
      state.checkout_enabled = readCheckoutEnabled(action.payload);
      state.checkout_deviations = readCheckoutDeviations(action.payload);
      state.providers = copyProviders(action.payload.providers);
      state.device_info_api_url = action.payload.device_info_api_url || "";
      state.device_info_api_key = action.payload.device_info_api_key || "";
      state.device_info_api_secret = "";
      state.device_info_api_secret_set = !!action.payload.device_info_api_secret_set;
      state.device_info_version = action.payload.device_info_version || DEVICE_INFO_VERSION_DEFAULT;
      state.device_info_refresh_hours = action.payload.device_info_refresh_hours > 0
      										?	action.payload.device_info_refresh_hours
      										:	DEVICE_INFO_REFRESH_HOURS_DEFAULT;
      // Explicit typeof: a `|| 0` fallback here would be fine for undefined but
      // reads as if 0 were merely "absent" — 0 is a meaningful value (use the
      // built-in default) and negatives are meaningful too (auto-purge off).
      state.deleted_retention_days = typeof action.payload.deleted_retention_days === "number"
      										?	action.payload.deleted_retention_days
      										:	0;
      // Same three-mode reading as above, and the same reason for the explicit
      // typeof: 0 means "use the built-in default", not "absent".
      state.audit_retention_days = typeof action.payload.audit_retention_days === "number"
      										?	action.payload.audit_retention_days
      										:	0;
      state.loading = false;
    },
  },
});

// §0.1 compatibility — a server older than 0.7.18ubuntu3 has no switch and no
// `checkout_enabled` key, and the Checkout existed there unswitched, so a
// MISSING key reads as ON. Only an explicit `false` switches it off. The
// `mw_light_enabled` of 0.7.18ubuntu2 is gone and deliberately ignored.
export function readCheckoutEnabled(payload: any): boolean {
  return !(payload && payload.checkout_enabled === false);
}

// §3.2 — true only once /master.json has answered AND said off, so nothing on
// the admin pages greys out on a guess. The room panel uses it as a first word
// only and follows the `/checkout.json` answer once it has one.
export function checkoutSwitchedOff(master: MasterInfo | null | undefined): boolean {
  return	!!master &&
			!master.loading &&
			master.checkout_enabled === false;
}

// The mirror image: true only once /master.json has answered and the Checkout
// is on (a missing key counts as on, as above). Before the answer — and for
// non-admins, who cannot read /master.json — this is false.
export function checkoutSwitchedOn(master: MasterInfo | null | undefined): boolean {
  return	!!master &&
			!master.loading &&
			master.checkout_enabled !== false;
}

// #91 §0.8 / §4.1 — `checkout_deviations`, defended row by row. An older
// server sends none: an empty list, which the page hides.
export function readCheckoutDeviations(payload: any): CheckoutDeviation[] {
  const raw = payload ? payload.checkout_deviations : null;

  if (!Array.isArray(raw))
    return [];

  return raw
    .filter((d: any) =>	d &&
						typeof d === "object" &&
						typeof d.id === "string" &&
						typeof d.checkout_enabled === "boolean")
    .map((d: any) => ({
      id:				d.id,
      name:				typeof d.name === "string" ? d.name : "",
      checkout_enabled:	d.checkout_enabled,
    }));
}

// #91 §0.8 / §3.4a — the Checkout is ON for this hotel: its own value when it
// has one, else System settings (which is false until /master.json answered,
// and for non-admins, who cannot read it). Long Stay is hidden while true.
export function hotelCheckoutOn(own: boolean | undefined, master: MasterInfo | null | undefined): boolean {
  return own !== undefined ? own : checkoutSwitchedOn(master);
}

// The mirror image, with the same "not known yet" caution: the Netflix box is
// greyed while true.
export function hotelCheckoutOff(own: boolean | undefined, master: MasterInfo | null | undefined): boolean {
  return own !== undefined ? !own : checkoutSwitchedOff(master);
}

// #91 §0.8 / §3.1a — the hotel's Checkout button on Settings → Executives.
// It works like a room setting against its hotel's.
export type HotelCheckoutStatus =
  | "follows_on"		// "Checkout follows System settings (on)."
  | "follows_off"		// "Checkout follows System settings (off)."
  | "only_on"			// "Checkout is on for this hotel only (System settings: off)."
  | "only_off";			// "Checkout is off for this hotel only (System settings: on)."

export type HotelCheckoutButton = "disable" | "enable" | "follow";

export interface HotelCheckoutState {
  differs:			boolean;
  status:			HotelCheckoutStatus;
  button:			HotelCheckoutButton;
  sets:				boolean;	// the checkout_enabled value the button writes
}

export function hotelCheckoutState(own: boolean | undefined, system: boolean): HotelCheckoutState {
  // A stored value equal to System settings (possible after System settings
  // was switched) counts as "follows".
  const differs =	own !== undefined &&
					own !== system;

  if (differs)
    return {
      differs:	true,
      status:	own ? "only_on" : "only_off",
      button:	"follow",
      // The System settings value: the server then removes the hotel's own.
      sets:		system,
    };

  return system
    ?	{ differs: false, status: "follows_on",  button: "disable", sets: false }
    :	{ differs: false, status: "follows_off", button: "enable",  sets: true };
}

export function copyProviderConfig(prov: ProviderConfig): ProviderConfig {
  return {
    name:			prov.name || "",
    type:			prov.type || "",
    base_url:		prov.base_url || "",
    country:		prov.country || "",
    disabled:		!!prov.disabled,
    creds_set:		!!prov.creds_set,
    urls:			copyProviderUrls(prov.urls),
  };
}

export function copyProviders(providers: any): ProviderConfig[] {
  return	Array.isArray(providers)
  			?	providers.map(copyProviderConfig)
  			:	[];
}

export function getNewProviderConfig(): ProviderConfig {
  return {
    name:			"",
    type:			PROVIDER_TYPES[0].value,
    base_url:		PROVIDER_BASE_URLS[PROVIDER_TYPES[0].value] || "",
    country:		"",
    disabled:		false,
    creds_set:		false,
    urls:			[],
  };
}

// NOTE: deleted_retention_days, audit_retention_days, providers and epg_sources
// are deliberately NOT copied. /master.json is a pointer patch, so an omitted
// key means "keep" — carrying these back from a stale whole-object buffer would
// roll back another admin's change to a field this page never showed.
export function copyMasterInfo(master: MasterInfo): MasterInfo {
  return {
    epg_url:			master.epg_url,
    smtp_host:			master.smtp_host,
    smtp_port:			master.smtp_port,
    smtp_username:		master.smtp_username,
    smtp_password:		master.smtp_password,
    smtp_password_set:	master.smtp_password_set,
    smtp_from:			master.smtp_from,
    smtp_from_name:		master.smtp_from_name,
    smtp_starttls:		master.smtp_starttls,
    public_base_url:	master.public_base_url,
    global_2fa_disabled:	master.global_2fa_disabled,
  };
}

export function getNewMasterInfo(loading?: boolean): MasterInfo {
  return {
    epg_url:			"",
    epg_sources:		[],
    smtp_host:			"",
    smtp_port:			0,
    smtp_username:		"",
    smtp_password:		"",
    smtp_password_set:	false,
    smtp_from:			"",
    smtp_from_name:		"",
    smtp_starttls:		true,	// most relays require this in 2026
    public_base_url:	"",
    global_2fa_disabled:	false,	// 2FA ENABLED by default
    providers:			[],
    device_info_api_url:	"",
    device_info_api_key:	"",
    device_info_api_secret:	"",
    device_info_api_secret_set:	false,
    device_info_version:	DEVICE_INFO_VERSION_DEFAULT,
    device_info_refresh_hours:	DEVICE_INFO_REFRESH_HOURS_DEFAULT,
    deleted_retention_days:	0,		// 0 = server default (180 days)
    audit_retention_days:	0,		// 0 = server default (180 days)
    loading:			loading,
  };
}
